import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { firstValueFrom } from 'rxjs';

export interface TermsSection {
  section_title: string;
  section_content: string;
  is_independent?: boolean;
}

/** Category sections marked independent are excluded from combined/full terms views. */
export function getCombinedCategorySections(
  sections: TermsSection[] = [],
): TermsSection[] {
  return sections.filter((section) => !section.is_independent);
}

export function getIndependentCategorySections(
  sections: TermsSection[] = [],
): TermsSection[] {
  return sections.filter((section) => !!section.is_independent);
}

export interface TermsRecord {
  id: string;
  sections: TermsSection[];
  created_at?: string;
  updated_at?: string;
}

export interface CategoryTermsRecord extends TermsRecord {
  category_id: string;
}

export interface FullCategoryTermsResponse {
  global: TermsRecord | null;
  category: CategoryTermsRecord | null;
}

@Injectable({
  providedIn: 'root',
})
export class AppliedTermsService {
  private readonly endpoint = 'rate-manager/provider/terms-and-conditions';

  constructor(private apiService: ApiService) {}

  async getGlobalTerms(): Promise<TermsRecord | null> {
    try {
      const response: any = await firstValueFrom(
        this.apiService.get(`${this.endpoint}/global`, {}, true),
      );
      return response?.data ?? response ?? null;
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  }

  async createGlobalTerms(sections: TermsSection[]): Promise<TermsRecord> {
    const response: any = await firstValueFrom(
      this.apiService.post(`${this.endpoint}/global`, { sections }),
    );
    return response?.data ?? response;
  }

  async updateGlobalTerms(sections: TermsSection[]): Promise<TermsRecord> {
    const response: any = await firstValueFrom(
      this.apiService.put(`${this.endpoint}/global`, { sections }),
    );
    return response?.data ?? response;
  }

  async deleteGlobalTerms(): Promise<void> {
    await firstValueFrom(
      this.apiService.delete(`${this.endpoint}/global`, {}, true),
    );
  }

  async getCategoryTerms(
    categoryId: string,
  ): Promise<CategoryTermsRecord | null> {
    try {
      const response: any = await firstValueFrom(
        this.apiService.get(
          `${this.endpoint}/categories/${categoryId}`,
          {},
          true,
        ),
      );
      return response?.data ?? response ?? null;
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  }

  async getCategoryTermsFull(
    categoryId: string,
  ): Promise<FullCategoryTermsResponse> {
    try {
      const response: any = await firstValueFrom(
        this.apiService.get(
          `${this.endpoint}/categories/${categoryId}/full`,
          {},
          true,
        ),
      );
      return response?.data ?? response;
    } catch (error) {
      console.error('AppliedTermsService.getCategoryTermsFull error:', error);
      throw error;
    }
  }

  async createCategoryTerms(
    categoryId: string,
    sections: TermsSection[],
  ): Promise<CategoryTermsRecord> {
    const response: any = await firstValueFrom(
      this.apiService.post(`${this.endpoint}/categories/${categoryId}`, {
        sections,
      }),
    );
    return response?.data ?? response;
  }

  async updateCategoryTerms(
    categoryId: string,
    sections: TermsSection[],
  ): Promise<CategoryTermsRecord> {
    const response: any = await firstValueFrom(
      this.apiService.put(`${this.endpoint}/categories/${categoryId}`, {
        sections,
      }),
    );
    return response?.data ?? response;
  }

  async deleteCategoryTerms(categoryId: string): Promise<void> {
    await firstValueFrom(
      this.apiService.delete(
        `${this.endpoint}/categories/${categoryId}`,
        {},
        true,
      ),
    );
  }
}
